<template>
    <div class="enquiry-form">
        <AuthFormInModal
            :is-open="!!enquiryType && !hideForm"
            :registration-disabled="fetching"
            :registration-errors="errors"
            :content
            @close="closeEnquiryForm"
            @register="submit($event)"
            @login="submit(() => {})"
        >
        </AuthFormInModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useEnquiryFormStore } from '@/stores/enquiryForm'

import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useHandleEnquiryResponse } from '@/composables/enquiry/useHandleEnquiryResponse'
import { useRegionPlugin } from '@/plugins/region'

import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useCreateEnquiryNewCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryNewCustomer.generated.ts'
import { useCreateEnquiryExistingCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryExistingCustomer.generated.ts'

import AuthFormInModal from '../user/auth/AuthFormInModal.vue'
import { useCurrentUrl } from '@/plugins/current-url.ts'
import { useInit } from '@/plugins/init/use-init.ts'
import { assertNever } from '@/helpers'
import type { EnquiryType } from '@/graphql/api'

const enquiryFormStore = useEnquiryFormStore()
const { closeEnquiryForm } = enquiryFormStore
const { enquiryType, enquiryImage, isFetching } = storeToRefs(enquiryFormStore)

const { enquiryFormContent } = useInit()

const content = computed(() => {
    if (!enquiryType.value) {
        return undefined
    }

    switch (enquiryType.value) {
        case 'BLOG_POST':
            return enquiryFormContent.value['enquiry-type-blog-post']

        case 'ENGAGEMENT_RING':
            return enquiryFormContent.value['enquiry-type-engagement-ring']

        case 'ENGAGEMENT_RING_BROWSE':
            return enquiryFormContent.value[
                'enquiry-type-engagement-ring-browse'
            ]

        case 'ENGAGEMENT_RING_IMAGE_ATTACHED_LANDING':
            return enquiryFormContent.value[
                'enquiry-type-engagement-ring-image-attached-landing'
            ]

        case 'JEWELLERY_PRODUCT_LANDING_GENERIC':
            return enquiryFormContent.value[
                'enquiry-type-jewellery-product-landing-generic'
            ]

        case 'LOCATION_SIGNUP':
            return enquiryFormContent.value['enquiry-type-location-signup']

        case 'WEDDING_RING':
            return enquiryFormContent.value['enquiry-type-wedding-ring']

        case 'WEDDING_RING_BESPOKE_LANDING':
            return enquiryFormContent.value[
                'enquiry-type-wedding-ring-bespoke-landing'
            ]
        default:
            return assertNever(enquiryType.value, undefined)
    }
})

const commissionJunction = getCommissionJunctionInput()

const currentUrl = useCurrentUrl()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()
const { isLogged } = useAuth()

const newCustomerResponse = useCreateEnquiryNewCustomerMutation()
const { executeMutation: newCustomerMutation, fetching: newCustomerFetching } =
    newCustomerResponse

const existingCustomerResponse = useCreateEnquiryExistingCustomerMutation()
const {
    executeMutation: existingCustomerMutation,
    fetching: existingCustomerFetching,
} = existingCustomerResponse

const fetching = computed(
    () => newCustomerFetching.value || existingCustomerFetching.value
)

watch(fetching, (n) => (isFetching.value = n))

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryExistingCustomer'
    ),
    createResponseAndPath(newCustomerResponse, 'createEnquiryNewCustomer'),
])

const { handleEnquiryResponse } = useHandleEnquiryResponse()

const enquiryInput = computed(() => {
    if (!enquiryType.value) {
        return null
    }

    const uri = currentUrl.value.href

    return {
        baseInput: {
            uri,
            currency: currency.value,
            region: regionCode.value,
        },
        image: enquiryImage.value,
        uri,
        type: enquiryType.value,
        commissionJunction,
    }
})

const hideForm = ref(false)

watch(
    enquiryType,
    (type) => {
        const input = enquiryInput.value

        if (!input) {
            return
        }

        if (!type) {
            return
        }

        if (!isLogged.value) {
            return
        }

        hideForm.value = true

        existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                hideForm.value = false
                // Error state should be displayed at this point
                return
            }

            if (data && data.createEnquiryExistingCustomer) {
                handleEnquiryResponse({
                    data: {
                        customer: undefined,
                    },
                    error,
                    redirect: getRedirect(type),
                })

                closeEnquiryForm()
                hideForm.value = false
            }
        })
    },
    { flush: 'post' }
)

const { customerRegistrationInput, googleSignInToken, facebookSignInToken } =
    storeToRefs(useCustomerRegistrationInputStore())

const isAdmin = useIsAdmin()

const doSubmit = () => {
    const input = enquiryInput.value

    if (!input) {
        return
    }

    if (isLogged.value) {
        return existingCustomerMutation({ input }).then(({ data, error }) => {
            if (error) {
                return undefined
            }

            return data?.createEnquiryExistingCustomer
        })
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return newCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ data, error }) => {
        if (error) {
            googleSignInToken.value = null
            facebookSignInToken.value = null

            return undefined
        }

        return data?.createEnquiryNewCustomer
    })
}

const getRedirect = (enquiryType: EnquiryType | null) => {
    switch (enquiryType) {
        case 'ENGAGEMENT_RING':
        case 'ENGAGEMENT_RING_IMAGE_ATTACHED_LANDING':
            switch (regionCode.value) {
                case 'UK':
                    return '/type-form/view/dHNtS0jG'
                case 'US':
                    return '/us/type-form/view/R1ZlBnod'
                case 'INT':
                    return '/int/type-form/view/WuvS2Sd5'
                default:
                    return undefined
            }
        case 'LOCATION_SIGNUP':
            return '/about-us/contacts'
        default:
            return undefined
    }
}

const submit = (successCallback: () => void) =>
    doSubmit()?.then((data) => {
        if (!data) {
            // Error state should be displayed at this point
            return
        }

        const redirect = getRedirect(enquiryType.value)

        handleEnquiryResponse({
            data:
                data.__typename === 'EnquiryResponseExistingCustomer'
                    ? undefined
                    : data,
            error: undefined,
            redirect,
        })

        if (
            data.__typename === 'EnquiryResponseNewCustomer' &&
            data.customer.login
        ) {
            successCallback()
        }

        closeEnquiryForm()
    })
</script>
